import { useMemo } from "react";
import { gql } from "urql";
import type { TypedDocumentNode } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatDayMonthYear, formatCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import type { OrderInvoiceStatus } from "@acto/gql/graphql";

const PAGE_SIZE = 10;

interface OrderNode {
  id: string;
  date: string;
  incomingOrder: number | null;
  externalReference: string;
  invoiceStatus: OrderInvoiceStatus | null;
}

interface OrdersConnectionTotals {
  incomingOrder: number | null;
}

interface OrdersQueryData {
  clients: {
    edges: {
      node: {
        id: string;
        orders: {
          edges: { cursor: string; node: OrderNode }[];
          pageInfo: { hasNextPage: boolean; endCursor: string | null };
          totals: OrdersConnectionTotals | null;
        } | null;
      };
    }[];
  } | null;
}

interface OrdersVariables {
  clientId: string;
  first: number;
  after?: string | null;
  dateRange?: { startDate: string; endDate: string };
  invoiceStatuses?: readonly OrderInvoiceStatus[];
  orderNumber?: string;
  productId?: string;
}

const ClientOrdersQuery: TypedDocumentNode<OrdersQueryData, OrdersVariables> = gql`
  query GetClientOrders(
    $clientId: ID!
    $first: Int
    $after: String
    $dateRange: DateRangeInput
    $invoiceStatuses: [OrderInvoiceStatus!]
    $orderNumber: String
    $productId: ID
  ) {
    clients(first: 1, filter: { id: $clientId }) {
      edges {
        node {
          id
          orders(
            first: $first
            after: $after
            dateRange: $dateRange
            invoiceStatuses: $invoiceStatuses
            orderNumber: $orderNumber
            productId: $productId
          ) {
            edges {
              cursor
              node {
                id
                date
                incomingOrder
                externalReference
                invoiceStatus
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
            totals {
              incomingOrder
            }
          }
        }
      }
    }
  }
`;

export interface OrderItem {
  id: string;
  externalReference: string;
  date: string;
  formattedDate: string;
  incomingOrder: number | null;
  formattedIncomingOrder: string;
  invoiceStatus: OrderInvoiceStatus | null;
}

export interface ClientOrders {
  isPending: boolean;
  isEmpty: boolean;
  error: Error | undefined;
  items: OrderItem[];
  totalIncomingOrder: number;
  formattedTotalIncomingOrder: string;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  isLoading: boolean;
}

export function useClientOrders(
  clientId: string | undefined,
  dateRange?: { startDate: string; endDate: string } | null,
  invoiceStatuses?: readonly OrderInvoiceStatus[],
  orderNumber?: string,
  productId?: string,
): ClientOrders {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const rawOrders = usePagination<OrdersQueryData, OrdersVariables, OrderNode, OrdersConnectionTotals>({
    query: ClientOrdersQuery,
    limit: PAGE_SIZE,
    variables: {
      clientId: clientId ?? "",
      dateRange: dateRange ?? undefined,
      invoiceStatuses,
      orderNumber: orderNumber || undefined,
      productId,
    },
    enabled: !!clientId,
    connection: (data) =>
      data.clients?.edges?.[0]?.node?.orders ?? {
        edges: [],
        pageInfo: { hasNextPage: false, endCursor: null },
      },
    metadata: (data) => data.clients?.edges?.[0]?.node?.orders?.totals ?? undefined,
  });

  const { data: orders, hasNextPage, fetchNextPage, isFetching, error, metadata } = rawOrders;

  const items = (orders ?? []).map((order) => ({
    id: order.id,
    externalReference: order.externalReference,
    date: order.date,
    formattedDate: formatDayMonthYear(order.date, locale),
    incomingOrder: order.incomingOrder ?? null,
    formattedIncomingOrder: order.incomingOrder != null ? formatCurrency(order.incomingOrder, currency, locale) : "—",
    invoiceStatus: order.invoiceStatus ?? null,
  }));

  const totalIncomingOrder = useMemo(() => metadata?.incomingOrder ?? 0, [metadata?.incomingOrder]);

  const formattedTotalIncomingOrder = formatCurrency(totalIncomingOrder, currency, locale);

  const isFirstPageLoading = isFetching && items.length === 0 && metadata?.incomingOrder === undefined;

  return {
    isLoading: isFetching && metadata?.incomingOrder === undefined,
    isPending: isFirstPageLoading,
    isEmpty: !isFirstPageLoading && !error && items.length === 0,
    error,
    items,
    totalIncomingOrder,
    formattedTotalIncomingOrder,
    hasMore: hasNextPage,
    isLoadingMore: isFetching && items.length > 0,
    loadMore: fetchNextPage,
  };
}
